import { IObservableArray, observable } from "mobx";

// Needs to be the same value as the genome-annotation-search-service k value
export const KMER_LEN = 31;

const TRIGRAM_MIN_TOKEN_LEN = 3;
const TRIGRAM_TOKEN_REGEX = /[A-Za-z0-9]+/g;

export const TRIGRAM_NOT_INDEXABLE_MESSAGE = "3+ consecutive alphanumeric characters required";

export function isTrigramIndexable(query: string): boolean {
  return (query.match(TRIGRAM_TOKEN_REGEX) ?? []).some(
    (token) => token.length >= TRIGRAM_MIN_TOKEN_LEN
  );
}

export interface SearchFilterProps {
  key: SearchFilterKeys;
  val: SearchFilterValue;
  title: string;
  disabled?: boolean;
}

export type SearchFilterValue = SearchFilters[keyof SearchFilters];

export type SearchFilters = {
  tags: IObservableArray<string>;
  isolation: null | string;
  type_strain: null | string;
  bsl: null | string;
  taxon_id: null | string;
  text: null | string;
  catalog_number: null | string;
  strain_name: null | string;
  taxonomy: null | string;
  gene: null | string;
  sequence: null | string;
  related_genome: null | string;
  cell_line_name: null | string;
  tissue: null | string;
  disease: null | string;
};

export const getDefaultSearchObject = (): SearchFilters => {
  return {
    tags: observable.array<string>([]),
    isolation: null,
    type_strain: null,
    bsl: null,
    taxon_id: null,
    text: null,
    catalog_number: null,
    taxonomy: null,
    gene: null,
    strain_name: null,
    sequence: null,
    related_genome: null,
    cell_line_name: null,
    tissue: null,
    disease: null,
  };
};

export type SearchFilterKeys = keyof SearchFilters;

export const GLOBAL_SEARCH_SECTIONS: SearchFilterProps[] = [
  { title: "Search Microbe Products:", key: "text", val: null },
  { title: "Search Cell Products:", key: "cell_line_name", val: null },
];

export function getMicrobeSearchSections(withUnusequenced: boolean): SearchFilterProps[] {
  return [
    { title: "Name", key: "text", val: null },
    { title: "Catalog Number", key: "catalog_number", val: null },
    {
      title: "Strain Alias",
      key: "strain_name",
      val: null,
      disabled: withUnusequenced,
    },
    { title: "Tag", key: "tags", val: null, disabled: withUnusequenced },
    { title: "Taxonomy", key: "taxonomy", val: null },
    { title: "Isolation", key: "isolation", val: null, disabled: withUnusequenced },
    { title: "Type Strain", key: "type_strain", val: null, disabled: withUnusequenced },
    { title: "Biosafety Level", key: "bsl", val: null, disabled: withUnusequenced },
    { title: "Gene", key: "gene", val: null, disabled: withUnusequenced },
    {
      title: `Search for genomes containing the sequence (minimum ${KMER_LEN} bases):`,
      key: "sequence",
      val: null,
      disabled: withUnusequenced,
    },
  ];
}

export const CELL_BIO_SEARCH_SECTIONS: SearchFilterProps[] = [
  { title: "Cell Line Name", key: "cell_line_name", val: null },
  { title: "ATCC Product Page", key: "catalog_number", val: null },
  { title: "Tissue", key: "tissue", val: null },
  { title: "Disease", key: "disease", val: null },
  { title: "Taxonomy", key: "taxonomy", val: null },
];
